import { CustomLoaderSmall } from "@/client/components/loaders";
import { PROJECT_CONFIG } from "@/config";
import { MOBILE_BREAKPOINT } from "@/constants";
import { SignInButton, useAuth, UserButton } from "@clerk/nextjs";
import {
  ActionIcon,
  Anchor,
  AppShell,
  Burger,
  Flex,
  Group,
  Stack,
  Text,
  ThemeIcon,
  UnstyledButton,
  useComputedColorScheme,
  useMantineColorScheme,
} from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import {
  IconBrandReddit,
  IconCards,
  IconMoonStars,
  IconSearch,
  IconSun,
} from "@tabler/icons-react";
import type { ReactNode } from "react";
import React from "react";

export type NavbarLinkData = {
  icon: ReactNode;
  label: string;
  link: string;
};

const NAVBAR_LINKS: Array<NavbarLinkData> = [
  { icon: <IconSearch size={18} />, label: "Search", link: "/" },
  { icon: <IconCards size={18} />, label: "My Cards", link: "/me" },
  {
    icon: <IconBrandReddit size={18} />,
    label: "Subreddit",
    link: PROJECT_CONFIG.subreddit,
  },
];

const ShellHeader = ({
  icon,
  opened,
  toggleOpened,
}: {
  icon: ReactNode;
  opened: boolean;
  toggleOpened: () => void;
}) => {
  const auth = useAuth();

  // You normally can't use this in Next.js, but I have SSR disabled globally
  const colorScheme = useComputedColorScheme();
  const { toggleColorScheme } = useMantineColorScheme();

  return (
    <AppShell.Header>
      <Flex align="center" h="100%" ml="sm" mr="sm">
        <Group justify={"space-between"} w={"100%"}>
          <Group gap="xs">
            <Group mr="lg">
              <Burger
                opened={opened}
                onClick={toggleOpened}
                hiddenFrom={MOBILE_BREAKPOINT}
                size="sm"
              />

              <Anchor
                href={"/"}
                style={{
                  textDecoration: "none",
                  color: colorScheme === "dark" ? "white" : "black",
                }}
              >
                <Group gap={"xs"}>
                  {icon}
                  <Text>{PROJECT_CONFIG.name}</Text>
                </Group>
              </Anchor>
            </Group>

            <Group gap="lg" visibleFrom={MOBILE_BREAKPOINT}>
              {NAVBAR_LINKS.map((elem, i) => {
                return (
                  <Anchor
                    href={elem.link}
                    style={{
                      textDecoration: "none",
                      color: colorScheme === "dark" ? "darkgray" : "black",
                    }}
                    key={i}
                  >
                    <Group gap={"5px"}>
                      {elem.icon}
                      {elem.label}
                    </Group>
                  </Anchor>
                );
              })}
            </Group>
          </Group>
          <Group>
            <ActionIcon
              variant="outline"
              onClick={toggleColorScheme}
              title="Toggle Color Scheme"
            >
              {colorScheme === "dark" ? (
                <IconSun size="1.2rem" />
              ) : (
                <IconMoonStars size="1.2rem" />
              )}
            </ActionIcon>
            {!auth.isLoaded && <CustomLoaderSmall />}
            {auth.isSignedIn ? <UserButton /> : <SignInButton />}
          </Group>
        </Group>
      </Flex>
    </AppShell.Header>
  );
};

const NavbarLink = ({ icon, label, link }: NavbarLinkData) => {
  const colorScheme = useComputedColorScheme();
  return (
    <Anchor href={link} style={{ textDecoration: "none" }} p="sm">
      <UnstyledButton
        style={(theme) => ({
          display: "block",
          width: "100%",
          padding: `5px ${theme.spacing.sm}`,
          borderRadius: theme.radius.sm,
          color: colorScheme === "dark" ? theme.colors.dark[0] : theme.black,
          "&:hover": {
            backgroundColor:
              colorScheme === "dark"
                ? theme.colors.dark[6]
                : theme.colors.gray[0],
          },
        })}
      >
        <Group>
          <ThemeIcon>{icon}</ThemeIcon>
          <Text size="md">{label}</Text>
        </Group>
      </UnstyledButton>
    </Anchor>
  );
};

/**
 * TODO, KNOWN BUG: If you start at a mobile resolution, toggle the burger menu off, then expand to a non-mobile resolution, it breaks the render of the filters. I'm going with the assumption that this is not an important fix because users will only very infrequently be doing such a resize, but I should fix it eventually.
 */
export const Shell = ({
  children,
  icon,
}: {
  children: ReactNode | ReactNode[];
  icon: ReactNode;
}) => {
  const [opened, { toggle }] = useDisclosure();
  return (
    <AppShell
      header={{ height: 50 }}
      navbar={{
        width: 300,
        breakpoint: MOBILE_BREAKPOINT,
        collapsed: { desktop: true, mobile: !opened },
      }}
    >
      <ShellHeader icon={icon} opened={opened} toggleOpened={toggle} />
      <AppShell.Navbar>
        <Stack gap={0}>
          {NAVBAR_LINKS.map((link) => (
            <NavbarLink key={link.label} {...link} />
          ))}
        </Stack>
      </AppShell.Navbar>
      <AppShell.Main>{children}</AppShell.Main>
    </AppShell>
  );
};
